import { useState, useEffect } from "react";
import { businessHoursApi, hoursOverrideApi } from "../lib/api";
import { DAYS_OF_WEEK, cn } from "../lib/utils";
import { format } from "date-fns";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Clock, Save, CalendarIcon, CalendarClock, Plus, Pencil, Trash2, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "../components/ui/badge";

const BusinessHours = () => {
  const [hours, setHours] = useState([]);
  const [snapInterval, setSnapInterval] = useState(15);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Hours overrides state
  const [hoursOverrides, setHoursOverrides] = useState([]);
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [editingOverride, setEditingOverride] = useState(null);
  const [overrideForm, setOverrideForm] = useState({
    name: "",
    override_type: "single",
    single_date: new Date(),
    start_date: new Date(),
    end_date: new Date(),
    recurring_month: 12,
    recurring_day: 25,
    is_closed: false,
    open_time: "10:00",
    close_time: "18:00",
    use_per_day_hours: false,
    per_day_hours: [
      { day: "monday", is_closed: false, open_time: "10:00", close_time: "18:00" },
      { day: "tuesday", is_closed: false, open_time: "10:00", close_time: "18:00" },
      { day: "wednesday", is_closed: false, open_time: "10:00", close_time: "18:00" },
      { day: "thursday", is_closed: false, open_time: "10:00", close_time: "18:00" },
      { day: "friday", is_closed: false, open_time: "10:00", close_time: "18:00" },
      { day: "saturday", is_closed: false, open_time: "10:00", close_time: "18:00" },
      { day: "sunday", is_closed: false, open_time: "10:00", close_time: "18:00" },
    ],
  });

  // Day names for per-day hours display
  const DAY_NAMES = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
  const DAY_LABELS = { monday: "Mon", tuesday: "Tue", wednesday: "Wed", thursday: "Thu", friday: "Fri", saturday: "Sat", sunday: "Sun" };

  // Month names for recurring annual display
  const MONTH_NAMES = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  // Fetch business hours and overrides
  const fetchData = async () => {
    try {
      setLoading(true);
      const [hoursRes, overridesRes] = await Promise.all([
        businessHoursApi.get(),
        hoursOverrideApi.getAll()
      ]);
      setHours(hoursRes.data.hours);
      setSnapInterval(hoursRes.data.snap_interval || 15);
      setHoursOverrides(overridesRes.data);
    } catch (error) {
      console.error("Error fetching business hours:", error);
      toast.error("Failed to load business hours");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Update a day's hours
  const updateDay = (dayId, field, value) => {
    setHours(prev => prev.map(h => 
      h.day === dayId ? { ...h, [field]: value } : h
    ));
  };

  // Snap time to interval
  const snapTimeToInterval = (time) => {
    const [hours, minutes] = time.split(':').map(Number);
    const snappedMinutes = Math.round(minutes / snapInterval) * snapInterval;
    const adjustedHours = hours + Math.floor(snappedMinutes / 60);
    const finalMinutes = snappedMinutes % 60;
    return `${adjustedHours.toString().padStart(2, '0')}:${finalMinutes.toString().padStart(2, '0')}`;
  };

  // Handle time change with snapping
  const handleTimeChange = (dayId, field, value) => {
    const snappedTime = snapTimeToInterval(value);
    updateDay(dayId, field, snappedTime);
  };

  // Save changes
  const handleSave = async () => {
    try {
      setSaving(true);
      // Get current settings to preserve other fields
      const currentSettings = await businessHoursApi.get();
      await businessHoursApi.update({ 
        ...currentSettings.data,
        hours
      });
      toast.success("Business hours saved");
    } catch (error) {
      console.error("Error saving business hours:", error);
      toast.error("Failed to save business hours");
    } finally {
      setSaving(false);
    }
  };

  // Apply same hours to all open days
  const applyToAll = (dayId) => {
    const sourceDay = hours.find(h => h.day === dayId);
    if (!sourceDay) return;

    setHours(prev => prev.map(h => ({
      ...h,
      open_time: sourceDay.open_time,
      close_time: sourceDay.close_time,
    })));
    toast.success("Applied to all days");
  };

  // Hours override management
  const openOverrideDialog = (override = null) => {
    const defaultPerDayHours = [
      { day: "monday", is_closed: false, open_time: "10:00", close_time: "18:00" },
      { day: "tuesday", is_closed: false, open_time: "10:00", close_time: "18:00" },
      { day: "wednesday", is_closed: false, open_time: "10:00", close_time: "18:00" },
      { day: "thursday", is_closed: false, open_time: "10:00", close_time: "18:00" },
      { day: "friday", is_closed: false, open_time: "10:00", close_time: "18:00" },
      { day: "saturday", is_closed: false, open_time: "10:00", close_time: "18:00" },
      { day: "sunday", is_closed: false, open_time: "10:00", close_time: "18:00" },
    ];

    if (override) {
      setEditingOverride(override);
      setOverrideForm({
        name: override.name,
        override_type: override.override_type,
        single_date: override.single_date ? new Date(override.single_date) : new Date(),
        start_date: override.start_date ? new Date(override.start_date) : new Date(),
        end_date: override.end_date ? new Date(override.end_date) : new Date(),
        recurring_month: override.recurring_month || 12,
        recurring_day: override.recurring_day || 25,
        is_closed: override.is_closed || false,
        open_time: override.open_time || "10:00",
        close_time: override.close_time || "18:00",
        use_per_day_hours: override.use_per_day_hours || false,
        per_day_hours: override.per_day_hours || defaultPerDayHours,
      });
    } else {
      setEditingOverride(null);
      setOverrideForm({
        name: "",
        override_type: "single",
        single_date: new Date(),
        start_date: new Date(),
        end_date: new Date(),
        recurring_month: 12,
        recurring_day: 25,
        is_closed: false,
        open_time: "10:00",
        close_time: "18:00",
        use_per_day_hours: false,
        per_day_hours: defaultPerDayHours,
      });
    }
    setOverrideDialogOpen(true);
  };

  // Update a specific day in per_day_hours
  const updatePerDayHour = (dayName, field, value) => {
    setOverrideForm(prev => ({
      ...prev,
      per_day_hours: prev.per_day_hours.map(d => 
        d.day === dayName ? { ...d, [field]: value } : d
      )
    }));
  };

  // Apply same hours to weekdays or weekend
  const applyToWeekdays = () => {
    const source = overrideForm.per_day_hours.find(d => d.day === "monday");
    if (!source) return;
    setOverrideForm(prev => ({
      ...prev,
      per_day_hours: prev.per_day_hours.map(d => 
        ["monday", "tuesday", "wednesday", "thursday", "friday"].includes(d.day)
          ? { ...d, is_closed: source.is_closed, open_time: source.open_time, close_time: source.close_time }
          : d
      )
    }));
  };

  const applyToWeekend = () => {
    const source = overrideForm.per_day_hours.find(d => d.day === "saturday");
    if (!source) return;
    setOverrideForm(prev => ({
      ...prev,
      per_day_hours: prev.per_day_hours.map(d => 
        ["saturday", "sunday"].includes(d.day)
          ? { ...d, is_closed: source.is_closed, open_time: source.open_time, close_time: source.close_time }
          : d
      )
    }));
  };

  const handleSaveOverride = async () => {
    if (!overrideForm.name.trim()) {
      toast.error("Override name is required");
      return;
    }

    // Prepare data based on type
    const data = {
      name: overrideForm.name,
      override_type: overrideForm.override_type,
      is_closed: overrideForm.use_per_day_hours ? false : overrideForm.is_closed,
      open_time: (overrideForm.use_per_day_hours || overrideForm.is_closed) ? null : overrideForm.open_time,
      close_time: (overrideForm.use_per_day_hours || overrideForm.is_closed) ? null : overrideForm.close_time,
    };

    if (overrideForm.override_type === "single") {
      data.single_date = format(overrideForm.single_date, "yyyy-MM-dd");
    } else if (overrideForm.override_type === "range") {
      data.start_date = format(overrideForm.start_date, "yyyy-MM-dd");
      data.end_date = format(overrideForm.end_date, "yyyy-MM-dd");
      // Add per-day hours if enabled
      data.use_per_day_hours = overrideForm.use_per_day_hours;
      if (overrideForm.use_per_day_hours) {
        data.per_day_hours = overrideForm.per_day_hours;
      }
    } else if (overrideForm.override_type === "recurring_annual") {
      data.recurring_month = overrideForm.recurring_month;
      data.recurring_day = overrideForm.recurring_day;
    }

    try {
      if (editingOverride) {
        await hoursOverrideApi.update(editingOverride.id, data);
        toast.success("Hours override updated");
      } else {
        await hoursOverrideApi.create(data);
        toast.success("Hours override created");
      }
      setOverrideDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error("Error saving hours override:", error);
      const msg = error.response?.data?.detail || "Failed to save hours override";
      toast.error(msg);
    }
  };

  const handleDeleteOverride = async (override) => {
    if (!confirm(`Delete "${override.name}" hours override?`)) return;
    
    try {
      await hoursOverrideApi.delete(override.id);
      toast.success("Hours override deleted");
      fetchData();
    } catch (error) {
      console.error("Error deleting hours override:", error);
      toast.error("Failed to delete hours override");
    }
  };

  // Format override date display
  const formatOverrideDate = (override) => {
    if (override.override_type === "single") {
      return format(new Date(override.single_date), "MMM d, yyyy");
    } else if (override.override_type === "range") {
      return `${format(new Date(override.start_date), "MMM d")} - ${format(new Date(override.end_date), "MMM d, yyyy")}`;
    } else if (override.override_type === "recurring_annual") {
      return `${MONTH_NAMES[override.recurring_month - 1]} ${override.recurring_day} (every year)`;
    }
    return "";
  };

  return (
    <div className="space-y-6" data-testid="business-hours-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Business Hours</h1>
          <p className="text-muted-foreground mt-1">
            Configure when your business operates and set special hours for holidays
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-hours-btn">
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </>
          )}
        </Button>
      </div>

      {/* Regular Hours */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Regular Hours
          </CardTitle>
          <CardDescription>
            Set your standard operating hours for each day of the week. These times control the timeline display in the dashboard.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-3">
              {DAYS_OF_WEEK.map((day) => {
                const dayHours = hours.find(h => h.day === day.id) || {
                  is_open: true,
                  open_time: "10:00",
                  close_time: "23:00"
                };

                return (
                  <div
                    key={day.id}
                    className={cn(
                      "flex items-center gap-4 p-4 rounded-lg border transition-colors",
                      dayHours.is_open ? "bg-card" : "bg-muted/50 opacity-60"
                    )}
                    data-testid={`day-hours-${day.id}`}
                  >
                    {/* Day Name */}
                    <div className="w-28 flex-shrink-0">
                      <p className="font-medium">{day.name}</p>
                    </div>

                    {/* Open/Closed Toggle */}
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={dayHours.is_open}
                        onCheckedChange={(checked) => updateDay(day.id, 'is_open', checked)}
                        data-testid={`toggle-${day.id}`}
                      />
                      <Label className="text-sm text-muted-foreground w-14">
                        {dayHours.is_open ? "Open" : "Closed"}
                      </Label>
                    </div>

                    {/* Time Inputs */}
                    {dayHours.is_open && (
                      <>
                        <div className="flex items-center gap-2">
                          <Label className="text-sm text-muted-foreground">From</Label>
                          <Input
                            type="time"
                            value={dayHours.open_time}
                            onChange={(e) => handleTimeChange(day.id, 'open_time', e.target.value)}
                            step={snapInterval * 60}
                            className="w-32 font-mono"
                            data-testid={`open-time-${day.id}`}
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <Label className="text-sm text-muted-foreground">To</Label>
                          <Input
                            type="time"
                            value={dayHours.close_time}
                            onChange={(e) => handleTimeChange(day.id, 'close_time', e.target.value)}
                            step={snapInterval * 60}
                            className="w-32 font-mono"
                            data-testid={`close-time-${day.id}`}
                          />
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => applyToAll(day.id)}
                          className="ml-auto text-xs"
                          data-testid={`apply-all-${day.id}`}
                        >
                          Apply to all
                        </Button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Hours Overrides */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CalendarClock className="w-5 h-5" />
                Hours Overrides
              </CardTitle>
              <CardDescription>
                Override default hours for specific dates, date ranges, or recurring annual events (e.g., holidays, school breaks).
              </CardDescription>
            </div>
            <Button onClick={() => openOverrideDialog()} size="sm" data-testid="add-override-btn">
              <Plus className="w-4 h-4 mr-1" />
              Add Override
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {hoursOverrides.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
              <CalendarClock className="w-8 h-8 mb-2 opacity-50" />
              <p>No hours overrides configured</p>
              <p className="text-sm">Add overrides for holidays, special events, or seasonal hours</p>
            </div>
          ) : (
            <div className="space-y-3">
              {hoursOverrides.map((override) => (
                <div
                  key={override.id}
                  className="flex items-center gap-4 p-4 rounded-lg border bg-card"
                  data-testid={`override-${override.id}`}
                >
                  <div className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center",
                    override.is_closed ? "bg-red-100 dark:bg-red-950" : "bg-primary/10"
                  )}>
                    {override.is_closed ? (
                      <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                    ) : (
                      <CalendarClock className="w-5 h-5 text-primary" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">{override.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {formatOverrideDate(override)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {override.is_closed ? (
                      <Badge variant="destructive">Closed</Badge>
                    ) : override.use_per_day_hours ? (
                      <Badge variant="secondary">Per-day hours</Badge>
                    ) : (
                      <Badge variant="secondary" className="font-mono">
                        {override.open_time} - {override.close_time}
                      </Badge>
                    )}
                    <Badge variant="outline">
                      {override.override_type === "single" && "Single Date"}
                      {override.override_type === "range" && "Date Range"}
                      {override.override_type === "recurring_annual" && "Recurring"}
                    </Badge>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openOverrideDialog(override)}
                      data-testid={`edit-override-${override.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteOverride(override)}
                      className="text-destructive hover:text-destructive"
                      data-testid={`delete-override-${override.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Hours Override Dialog */}
      <Dialog open={overrideDialogOpen} onOpenChange={setOverrideDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden">
          <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-4">
            <DialogTitle>
              {editingOverride ? "Edit Hours Override" : "Add Hours Override"}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto overflow-x-hidden px-6">
            <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="override-name">Name</Label>
              <Input
                id="override-name"
                value={overrideForm.name}
                onChange={(e) => setOverrideForm({ ...overrideForm, name: e.target.value })}
                placeholder="e.g., Christmas Day, Summer Hours"
                data-testid="override-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Override Type</Label>
              <Select 
                value={overrideForm.override_type} 
                onValueChange={(v) => setOverrideForm({ ...overrideForm, override_type: v })}
              >
                <SelectTrigger data-testid="override-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="single">Single Date</SelectItem>
                  <SelectItem value="range">Date Range</SelectItem>
                  <SelectItem value="recurring_annual">Recurring Annual</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Single Date */}
            {overrideForm.override_type === "single" && (
              <div className="space-y-2">
                <Label>Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(overrideForm.single_date, "PPP")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={overrideForm.single_date}
                      onSelect={(date) => date && setOverrideForm({ ...overrideForm, single_date: date })}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            )}

            {/* Date Range */}
            {overrideForm.override_type === "range" && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {format(overrideForm.start_date, "MMM d")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={overrideForm.start_date}
                        onSelect={(date) => date && setOverrideForm({ ...overrideForm, start_date: date })}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {format(overrideForm.end_date, "MMM d")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={overrideForm.end_date}
                        onSelect={(date) => date && setOverrideForm({ ...overrideForm, end_date: date })}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            )}

            {/* Recurring Annual */}
            {overrideForm.override_type === "recurring_annual" && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Month</Label>
                  <Select 
                    value={overrideForm.recurring_month.toString()} 
                    onValueChange={(v) => setOverrideForm({ ...overrideForm, recurring_month: parseInt(v) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MONTH_NAMES.map((month, i) => (
                        <SelectItem key={i} value={(i + 1).toString()}>{month}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Day</Label>
                  <Select 
                    value={overrideForm.recurring_day.toString()} 
                    onValueChange={(v) => setOverrideForm({ ...overrideForm, recurring_day: parseInt(v) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 31 }, (_, i) => (
                        <SelectItem key={i} value={(i + 1).toString()}>{i + 1}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Per-day hours toggle for date ranges */}
            {overrideForm.override_type === "range" && (
              <div className="flex items-center justify-between p-3 rounded-lg border">
                <div>
                  <Label>Different hours per day</Label>
                  <p className="text-xs text-muted-foreground">Set different hours for each day of the week</p>
                </div>
                <Switch
                  checked={overrideForm.use_per_day_hours}
                  onCheckedChange={(checked) => setOverrideForm({ ...overrideForm, use_per_day_hours: checked, is_closed: false })}
                />
              </div>
            )}

            {/* Hours settings */}
            {!overrideForm.use_per_day_hours && (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 rounded-lg border">
                  <div>
                    <Label>Closed all day</Label>
                    <p className="text-xs text-muted-foreground">Business is closed on this date</p>
                  </div>
                  <Switch
                    checked={overrideForm.is_closed}
                    onCheckedChange={(checked) => setOverrideForm({ ...overrideForm, is_closed: checked })}
                    data-testid="override-closed-toggle"
                  />
                </div>

                {!overrideForm.is_closed && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Open Time</Label>
                      <Input
                        type="time"
                        value={overrideForm.open_time}
                        onChange={(e) => setOverrideForm({ ...overrideForm, open_time: e.target.value })}
                        className="font-mono"
                        data-testid="override-open-time"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Close Time</Label>
                      <Input
                        type="time"
                        value={overrideForm.close_time}
                        onChange={(e) => setOverrideForm({ ...overrideForm, close_time: e.target.value })}
                        className="font-mono"
                        data-testid="override-close-time"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Per-day hours configuration */}
            {overrideForm.use_per_day_hours && overrideForm.override_type === "range" && (
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={applyToWeekdays}>
                    Copy Mon to weekdays
                  </Button>
                  <Button variant="outline" size="sm" onClick={applyToWeekend}>
                    Copy Sat to weekend
                  </Button>
                </div>
                {DAY_NAMES.map((dayName) => {
                  const dayData = overrideForm.per_day_hours.find(d => d.day === dayName);
                  return (
                    <div key={dayName} className={cn(
                      "flex items-center gap-3 p-3 rounded-lg border",
                      dayData?.is_closed ? "bg-muted/50 opacity-60" : ""
                    )}>
                      <div className="w-12 font-medium">{DAY_LABELS[dayName]}</div>
                      <Switch
                        checked={!dayData?.is_closed}
                        onCheckedChange={(checked) => updatePerDayHour(dayName, 'is_closed', !checked)}
                      />
                      {!dayData?.is_closed && (
                        <>
                          <Input
                            type="time"
                            value={dayData?.open_time || "10:00"}
                            onChange={(e) => updatePerDayHour(dayName, 'open_time', e.target.value)}
                            className="w-28 font-mono"
                          />
                          <span className="text-muted-foreground">to</span>
                          <Input
                            type="time"
                            value={dayData?.close_time || "18:00"}
                            onChange={(e) => updatePerDayHour(dayName, 'close_time', e.target.value)}
                            className="w-28 font-mono"
                          />
                        </>
                      )}
                      {dayData?.is_closed && (
                        <span className="text-sm text-muted-foreground">Closed</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            </div>
          </div>
          <DialogFooter className="flex-shrink-0 px-6 pb-6 pt-4 border-t">
            <Button variant="outline" onClick={() => setOverrideDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveOverride} data-testid="save-override-btn">
              {editingOverride ? "Update" : "Add"} Override
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BusinessHours;
